/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useContext } from 'react';

import { breakpoints } from '@coursera/cds-core';

import DegreeDescriptionPageContext from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionPageElementRefs } from 'bundles/degree-description/contexts/DegreeDescriptionPageElementRefsContext';
import useDegreeEoiSurveyProps from 'bundles/degree-description/hooks/useDegreeEoiSurveyProps';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import MultiStepEoiSurvey from 'bundles/expression-of-interest/components/ddp-multistep/MultiStepEoiSurvey';

type Props = {
  trackingIndex?: number;
};

const styles = {
  container: css`
    background: var(--cds-color-neutral-background-primary);
    border-radius: 8px;
    box-shadow: var(--cds-elevation-level2);
    max-width: unset;
    padding: var(--cds-spacing-300);

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-200);
    }
  `,
};

const ExperimentalEoiForm = ({ trackingIndex }: Props) => {
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const { setEoiFormAnchorRef } = useDegreeDescriptionPageElementRefs();
  const pageContext = useContext(DegreeDescriptionPageContext);
  const hasCompletedSurvey = Boolean(pageContext?.eoiSurveyData?.QualificationSurveySectionResponse.isComplete);
  const surveyProps = useDegreeEoiSurveyProps();
  const pageSectionRef = useGetViewPageSectionRef('eoi', trackingIndex);

  // pageSectionRef and setEoiFormAnchorRef are both stable across renders (a MutableRefObject and
  // a useState setter, respectively), so this merged ref callback keeps a stable identity too.
  const setFormRef = useCallback<(e: HTMLDivElement | null) => void>(
    (e) => {
      pageSectionRef.current = e;
      setEoiFormAnchorRef?.(e as HTMLElement);
    },
    [pageSectionRef, setEoiFormAnchorRef]
  );

  return (
    <div css={styles.container} ref={setFormRef} data-testid="experimental-eoi-form">
      <MultiStepEoiSurvey {...surveyProps} hasCompletedSurvey={hasCompletedSurvey} />
    </div>
  );
};

export default ExperimentalEoiForm;
